import { AccrediblePageMeta } from '@accredible-frontend-v2/models';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

const MAX_RECORDS = 10000;

@Component({
  selector: 'accredible-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnInit {
  private readonly _liveAnnouncer = inject(LiveAnnouncer);
  private readonly _language = inject(AccredibleLanguageService);

  private jumpedToPage: boolean;

  pageMeta = input<AccrediblePageMeta>({
    current_page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 1,
  });

  containerStyles = input<{ [key: string]: string }>({ justifyContent: 'flex-end' });
  jumpToPage = input(false);

  pageChange = output<number>();
  jumpToPageControl: FormControl<string>;

  constructor() {
    effect(() => {
      this._updateJumpToPageControl(this.pageMeta());
    });
  }

  ngOnInit() {
    this._initialiseJumpToPageControl();
  }

  getResultsFromPage(page: number): number {
    if (page <= 0 || this.pageMeta().total_count === 0) {
      return 0;
    }

    const pageSize = this.pageMeta().page_size;
    return (page - 1) * pageSize + 1;
  }

  getResultsToPage(page: number): number {
    const maxViewingTo: number = page * this.pageMeta().page_size;
    const to = Math.min(maxViewingTo, this.pageMeta().total_count);
    return isNaN(to) ? 0 : to;
  }

  onJumpToPage(): void {
    if (this.jumpToPageControl.invalid) {
      return;
    }

    const newPage = Number(this.jumpToPageControl.value);
    if (newPage === this.pageMeta().current_page) {
      return;
    }

    this.jumpedToPage = true;
    this.onPageChange(newPage);
  }

  onPageChange(page: number): void {
    this._announcePageChange(page);
    this.pageChange.emit(page);
  }

  private _initialiseJumpToPageControl(): void {
    this.jumpToPageControl = new FormControl('0');
  }

  private _announcePageChange(page: number) {
    this._liveAnnouncer.announce(
      this._language.translate('pagination.aria_viewing_from_to', {
        from: this.getResultsFromPage(page),
        to: this.getResultsToPage(page),
        totalCount: this.pageMeta().total_count,
      }),
    );
  }

  private _updateJumpToPageControl(pageMeta: AccrediblePageMeta) {
    if (this.jumpedToPage) {
      this.jumpedToPage = false;
      return;
    }

    this._setValue(pageMeta);
    this._setValidators(pageMeta);
  }

  private _setValidators(pageMeta: AccrediblePageMeta) {
    const validators = [Validators.pattern('^[0-9]*$'), Validators.min(1)];

    // Only add max validator if there are results
    if (pageMeta.total_count > 0) {
      validators.push(Validators.max(this._getMaxPages(pageMeta)));
    }

    this.jumpToPageControl.setValidators(validators);
    this.jumpToPageControl.updateValueAndValidity();
  }

  private _getMaxPages(pageMeta: AccrediblePageMeta): number {
    // Allow user to enter a page number greater than total pages when total count is greater than 10,000
    return pageMeta.total_count > MAX_RECORDS
      ? Math.ceil(pageMeta.total_count / pageMeta.page_size)
      : pageMeta.total_pages;
  }

  private _setValue(pageMeta: AccrediblePageMeta) {
    const hasResults = pageMeta.total_pages > 0;

    // Show 0 when loading
    const currentPage = hasResults ? pageMeta.current_page.toString() : '0';
    this.jumpToPageControl.setValue(currentPage, { emitEvent: false });

    if (hasResults) {
      this.jumpToPageControl.enable({ emitEvent: false });
    } else {
      this.jumpToPageControl.disable({ emitEvent: false });
    }
  }
}
